import type { Citation } from "../../../shared/citations/types";
import { extractIdsFromCitation } from "../../../shared/citations/ai/extractCitationIds";
import {
  addSlideTagsViaIntents,
  getSlideTagsViaIntents,
  listSlidesViaIntents,
} from "../../services/factset/intentBridgeService";

export const CITATION_POOL_TAG_KEY = "finster:citationPool";
export const CITATION_REFS_TAG_KEY = "finster:citationRefs";

const CURRENT_SCHEMA_VERSION = 1;

export interface CitationPoolEnvelope {
  schemaVersion: number;
  updatedAt: number;
  citations: Record<string, Citation>;
}

const inProcessPoolCache: Map<string, CitationPoolEnvelope> = new Map();

function emptyEnvelope(): CitationPoolEnvelope {
  return { schemaVersion: CURRENT_SCHEMA_VERSION, updatedAt: 0, citations: {} };
}

async function getSlideOneId(log?: (msg: string) => void): Promise<string | null> {
  try {
    const slides = await listSlidesViaIntents(log);
    if (!slides || slides.length === 0) return null;
    const first = slides.find((s) => s.index === 0) ?? slides[0];
    return first?.slideId ?? null;
  } catch (error) {
    log?.(`[citationPool] failed to resolve slide-1 id: ${stringifyError(error)}`);
    return null;
  }
}

function stringifyError(error: unknown): string {
  return error instanceof Error ? error.message : String(error);
}

function parseEnvelope(raw: string | undefined): CitationPoolEnvelope {
  if (!raw) return emptyEnvelope();
  try {
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return emptyEnvelope();
    const candidate = parsed as Partial<CitationPoolEnvelope>;
    const citations =
      candidate.citations && typeof candidate.citations === "object"
        ? (candidate.citations as Record<string, Citation>)
        : {};
    return {
      schemaVersion:
        typeof candidate.schemaVersion === "number" ? candidate.schemaVersion : CURRENT_SCHEMA_VERSION,
      updatedAt: typeof candidate.updatedAt === "number" ? candidate.updatedAt : 0,
      citations,
    };
  } catch {
    return emptyEnvelope();
  }
}

export async function readPool(log?: (msg: string) => void): Promise<CitationPoolEnvelope> {
  const slideOneId = await getSlideOneId(log);
  if (!slideOneId) return emptyEnvelope();

  try {
    const tags = await getSlideTagsViaIntents({ slideId: slideOneId, log });
    if (tags && typeof tags[CITATION_POOL_TAG_KEY] === "string") {
      const env = parseEnvelope(tags[CITATION_POOL_TAG_KEY]);
      inProcessPoolCache.set(slideOneId, env);
      return env;
    }
  } catch (error) {
    log?.(`[citationPool] readPool intent error: ${stringifyError(error)}`);
  }

  const cached = inProcessPoolCache.get(slideOneId);
  if (cached) return cached;

  return emptyEnvelope();
}

export async function mergeIntoPool(
  newCitations: Citation[],
  log?: (msg: string) => void
): Promise<void> {
  if (!Array.isArray(newCitations) || newCitations.length === 0) return;

  const slideOneId = await getSlideOneId(log);
  if (!slideOneId) {
    log?.("[citationPool] mergeIntoPool: no slide-1 id available; skipping.");
    return;
  }

  const envelope = await readPool(log);
  const now = Date.now();

  for (const citation of newCitations) {
    if (!citation || typeof citation !== "object") continue;
    const ids = extractIdsFromCitation(citation);
    for (let i = 0; i < ids.length; i++) {
      envelope.citations[ids[i]] = citation;
    }
  }

  envelope.schemaVersion = CURRENT_SCHEMA_VERSION;
  envelope.updatedAt = now;

  const serialized = JSON.stringify(envelope);
  inProcessPoolCache.set(slideOneId, envelope);

  try {
    await addSlideTagsViaIntents({
      slideId: slideOneId,
      tags: { [CITATION_POOL_TAG_KEY]: serialized },
      log,
    });
    log?.(
      `[citationPool] mergeIntoPool wrote ${serialized.length} bytes (${Object.keys(envelope.citations).length} uuids).`
    );
  } catch (error) {
    log?.(`[citationPool] mergeIntoPool write failed: ${stringifyError(error)}`);
  }
}

export function resetCitationPoolCache(): void {
  inProcessPoolCache.clear();
}

export async function resolveCitationsByIds(
  ids: string[],
  log?: (msg: string) => void
): Promise<Citation[]> {
  if (!Array.isArray(ids) || ids.length === 0) return [];
  const envelope = await readPool(log);
  const out: Citation[] = [];
  const seen = new Set<Citation>();
  for (const id of ids) {
    const citation = envelope.citations[id];
    if (citation && !seen.has(citation)) {
      seen.add(citation);
      out.push(citation);
    }
  }
  return out;
}
